import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import SiteShell from "@/components/SiteShell";
import ExitIntentModal from "@/components/ExitIntentModal";
import { ensureOpenAiPixel } from "@/lib/openaiPixel";
import { ensureLinkedInInsight } from "@/lib/linkedinInsight";
import { captureRefParam } from "@/lib/landingAttribution";

// Eagerly load the home page so first paint is fast for the most common entry point.
import Landing from "@/pages/Landing";

// Lazy-load every other route to reduce the initial JS bundle on first paint.
const Checklist = lazy(() => import("@/pages/Checklist"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Partners = lazy(() => import("@/pages/Partners"));
const AdminPartners = lazy(() => import("@/pages/AdminPartners"));
const AdminPress = lazy(() => import("@/pages/AdminPress"));
const Faq = lazy(() => import("@/pages/Faq"));
const Contact = lazy(() => import("@/pages/Contact"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const MagicLinkVerify = lazy(() => import("@/pages/MagicLinkVerify"));
const Intake = lazy(() => import("@/pages/Intake"));
const AnalysisView = lazy(() => import("@/pages/AnalysisView"));
const AdminLeads = lazy(() => import("@/pages/AdminLeads"));
const AdminLookup = lazy(() => import("@/pages/AdminLookup"));
const AdminMembers = lazy(() => import("@/pages/AdminMembers"));
const AdminReviews = lazy(() => import("@/pages/AdminReviews"));
const AdminBridge = lazy(() => import("@/pages/AdminBridge"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Account = lazy(() => import("@/pages/Account"));
const Briefings = lazy(() => import("@/pages/Briefings"));
const ResearchLayer = lazy(() => import("@/pages/ResearchLayer"));
const Drafting = lazy(() => import("@/pages/Drafting"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const Reviews = lazy(() => import("@/pages/Reviews"));
const SubmitReview = lazy(() => import("@/pages/SubmitReview"));
const Apply = lazy(() => import("@/pages/Apply"));
const VipAccess = lazy(() => import("@/pages/VipAccess"));
const RapidDiagnostic = lazy(() => import("@/pages/RapidDiagnostic"));
const BuilderSummary = lazy(() => import("@/pages/BuilderSummary"));
const Beta3Feedback = lazy(() => import("@/pages/Beta3Feedback"));
const PartnerLanding = lazy(() => import("@/pages/PartnerLanding"));
const MugenVip = lazy(() => import("@/pages/MugenVip"));
const EmtechVip = lazy(() => import("@/pages/EmtechVip"));
const Emtech = lazy(() => import("@/pages/Emtech"));
const Press = lazy(() => import("@/pages/Press"));
const PressRelease = lazy(() => import("@/pages/PressRelease"));
const Start = lazy(() => import("@/pages/Start"));
const Redeem = lazy(() => import("@/pages/Redeem"));
const Webinar = lazy(() => import("@/pages/Webinar"));
const Replay = lazy(() => import("@/pages/Replay"));
const Survey = lazy(() => import("@/pages/Survey"));
const UpgradeRedirect = lazy(() => import("@/pages/UpgradeRedirect"));
const BetaInvite = lazy(() => import("@/pages/BetaInvite"));
const CoreInvite = lazy(() => import("@/pages/CoreInvite"));
// Auth.jsx exports two named components; pull them out via dynamic-import resolver.
const LoginPage = lazy(() =>
  import("@/pages/Auth").then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import("@/pages/Auth").then((m) => ({ default: m.RegisterPage })),
);

/** Route-state objects hoisted so React doesn't see a new ref on every render. */
const PROTECTED_REDIRECT_STATE = (pathname) => ({ from: pathname });

/** Requires login. */
const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return <RouteFallback />;
  }
  if (!user) return <Navigate to="/login" state={PROTECTED_REDIRECT_STATE(loc.pathname)} replace />;
  return children;
};

/** Requires login AND workspace access (paid, admin, or single-pass holder). */
const PaidOnly = ({ children }) => {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return <RouteFallback />;
  }
  if (!user) return <Navigate to="/login" state={PROTECTED_REDIRECT_STATE(loc.pathname)} replace />;
  if (!user.has_access) return <Navigate to="/pricing" replace />;
  return children;
};

/** Lightweight, brand-consistent placeholder shown while a route chunk loads. */
const RouteFallback = () => (
  <div
    data-testid="route-fallback"
    className="max-w-[900px] mx-auto px-4 sm:px-6 py-20 font-mono text-xs uppercase tracking-[0.25em] text-ink/55"
  >
    Loading…
  </div>
);

/**
 * Route-level scroll behavior (SPA has no native page loads):
 * - Navigation WITHOUT a hash resets scroll to the top of the new page, so
 *   footer links never land mid-page.
 * - Navigation WITH a hash (e.g. /#how-it-works, /faq#refund-policy,
 *   /pricing#counsel-comparison) smooth-scrolls to that exact element,
 *   retrying briefly while lazy route chunks render.
 * Effects never run during build-time prerendering, so SSR is unaffected.
 */
const ScrollManager = () => {
  const { pathname, hash, key } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      let tries = 0;
      const timers = [];
      const attempt = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          // Corrective pass: images/embeds above the target that finish
          // loading after the first scroll shift the layout; re-align once
          // things have settled so the link lands exactly on the section.
          timers.push(setTimeout(() => {
            const t = document.getElementById(id);
            if (t) t.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 900));
        } else if (tries++ < 25) {
          timers.push(setTimeout(attempt, 120)); // lazy chunk still loading
        }
      };
      attempt();
      return () => timers.forEach(clearTimeout);
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    // location.key in the deps makes same-route clicks (e.g., the footer
    // link for the page you are already on) reset scroll too: every Link
    // click pushes a new history entry with a fresh key even when the
    // pathname/hash are identical.
  }, [pathname, hash, key]);
  return null;
};

/** Loads the ad measurement tags (OpenAI Ads pixel, LinkedIn Insight Tag)
    when the visitor reaches a public route via client-side navigation
    (initial public-page loads are covered by the snippets in
    public/index.html; each tag shares one loaded flag). */
const AdsPixelLoader = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    ensureOpenAiPixel(pathname);
    ensureLinkedInInsight(pathname);
  }, [pathname]);
  return null;
};

/** Honours ?ref=<partner> on any route, so a referral partner can link
    anywhere on the site and the visit is still attributed to them. Only known
    partner pages are honoured, and first-touch wins: an existing partner
    capture is never overwritten. */
const ReferralRefCapture = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    captureRefParam();
  }, [pathname, search]);
  return null;
};

/**
 * Everything inside the router: providers, chrome, and the route table.
 * Shared by the client app (BrowserRouter, below) and the build-time
 * prerender entry (StaticRouter in src/prerender-entry.jsx) so the
 * server-rendered markup and the hydrating client tree stay identical.
 */
export function AppShell() {
  return (
    <AuthProvider>
      <ScrollManager />
      <AdsPixelLoader />
      <ReferralRefCapture />
      <SiteShell>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/assessment" element={<Landing />} />
            <Route path="/checklist" element={<Checklist />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/sample-memo" element={<Navigate to="/checklist" replace />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/submit-review" element={<SubmitReview />} />
            <Route path="/apply" element={<Apply />} />
            <Route path="/vipaccess" element={<VipAccess />} />
            <Route path="/summary/:id" element={<BuilderSummary />} />
            <Route path="/beta3-feedback" element={<Beta3Feedback />} />
            <Route path="/diagnostic" element={<Protected><RapidDiagnostic /></Protected>} />
            <Route path="/partner-template" element={<PartnerLanding />} />
            <Route path="/partner/:slug" element={<PartnerLanding />} />
            <Route path="/mugenvip" element={<MugenVip />} />
            <Route path="/emtechvip" element={<EmtechVip />} />
            <Route path="/emtech" element={<Emtech />} />
            <Route path="/press" element={<Press />} />
            <Route path="/press/:slug" element={<PressRelease />} />
            <Route path="/start" element={<Start />} />
            <Route path="/redeem" element={<Redeem />} />
            <Route path="/webinar" element={<Webinar />} />
            <Route path="/replay" element={<Replay />} />
            <Route path="/survey" element={<Survey />} />
            <Route path="/upgrade" element={<UpgradeRedirect />} />
            {/* Retired Feb 2026 with the Regulatory Readiness Stage framework.
                Kept as redirects so results already shared keep working, and
                land on the invitation to take the assessment. */}
            <Route path="/assessment/stage-1" element={<Navigate to="/start" replace />} />
            <Route path="/assessment/stage-2" element={<Navigate to="/start" replace />} />
            <Route path="/assessment/stage-3" element={<Navigate to="/start" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/magic-link/verify" element={<MagicLinkVerify />} />
            <Route path="/beta-invite/:token" element={<BetaInvite />} />
            <Route path="/core-invite/:token" element={<CoreInvite />} />
            <Route path="/intake" element={<PaidOnly><Intake /></PaidOnly>} />
            <Route path="/analysis/:id" element={<PaidOnly><AnalysisView /></PaidOnly>} />
            <Route path="/dashboard" element={<PaidOnly><Dashboard /></PaidOnly>} />
            <Route path="/account" element={<Protected><Account /></Protected>} />
            <Route path="/briefings" element={<PaidOnly><Briefings /></PaidOnly>} />
            <Route path="/research" element={<Protected><ResearchLayer /></Protected>} />
            <Route path="/drafting" element={<Protected><Drafting /></Protected>} />
            <Route path="/admin/leads" element={<Protected><AdminLeads /></Protected>} />
            <Route path="/admin/lookup" element={<Protected><AdminLookup /></Protected>} />
            <Route path="/admin/members" element={<Protected><AdminMembers /></Protected>} />
            <Route path="/admin/reviews" element={<Protected><AdminReviews /></Protected>} />
            <Route path="/admin/partners" element={<Protected><AdminPartners /></Protected>} />
            <Route path="/admin/press" element={<Protected><AdminPress /></Protected>} />            <Route path="/admin/bridge" element={<Protected><AdminBridge /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </SiteShell>
      <ExitIntentModal />
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </div>
  );
}

export default App;
